import { ref, type ComputedRef, type Ref } from 'vue'
import { useRouter } from 'vue-router'

import type { PendingInvite } from '../models/auth.types'
import { AuthService } from '../service/AuthService'

import { useAccount, useAccountSession } from '@/features/account'
import { ApiError } from '@/shared/http'
import { useI18n } from '@/shared/i18n'
import { useToast } from '@/shared/ui'

const ACCOUNT_ALREADY_EXISTS = 'ACCOUNT_ALREADY_EXISTS'

export interface UsePendingInvites {
  invites: Ref<PendingInvite[]>
  selectedIds: Ref<number[]>
  userEmail: ComputedRef<string | undefined>
  loading: Ref<boolean>
  pending: Ref<boolean>
  error: Ref<string | null>
  load: () => Promise<void>
  toggle: (inviteId: number) => void
  accept: () => Promise<void>
  continueWithoutInvites: () => Promise<void>
}

export function usePendingInvites(): UsePendingInvites {
  const router = useRouter()
  const service = new AuthService()
  const account = useAccount()
  const session = useAccountSession()
  const toast = useToast()
  const { t } = useI18n()

  const invites = ref<PendingInvite[]>([])
  const selectedIds = ref<number[]>([])
  const loading = ref(false)
  const pending = ref(false)
  const error = ref<string | null>(null)

  async function finish(accountId?: number): Promise<void> {
    await session.boot(true)
    if (accountId && account.accountId.value !== accountId) {
      const acceptedAccount = account.accounts.value.find((item) => Number(item.id) === accountId)
      if (acceptedAccount) await account.selectAccount(acceptedAccount)
    }
    await router.replace({ name: 'Summary' })
  }

  async function load(): Promise<void> {
    loading.value = true
    error.value = null
    try {
      invites.value = await service.getPendingInvites()
      selectedIds.value = invites.value.length === 1 ? [invites.value[0]!.invite_id] : []
      if (invites.value.length === 0) await finish()
    } catch {
      error.value = 'auth.pendingInvites.loadError'
    } finally {
      loading.value = false
    }
  }

  function toggle(inviteId: number): void {
    selectedIds.value = selectedIds.value.includes(inviteId)
      ? selectedIds.value.filter((id) => id !== inviteId)
      : [...selectedIds.value, inviteId]
  }

  async function accept(): Promise<void> {
    if (pending.value || selectedIds.value.length === 0) return
    pending.value = true
    error.value = null
    try {
      const response = await service.acceptPendingInvites(selectedIds.value)
      if (response.accepted.length === 0) {
        toast.error(t('auth.pendingInvites.noneAccepted'))
        await load()
        return
      }
      if (response.failed.length > 0) {
        toast.warning(
          t('auth.pendingInvites.someFailed', {
            accepted: response.accepted.length,
            total: selectedIds.value.length,
          }),
        )
      }
      await finish(response.accepted[0]!.account)
    } catch {
      error.value = 'auth.pendingInvites.acceptError'
    } finally {
      pending.value = false
    }
  }

  async function continueWithoutInvites(): Promise<void> {
    if (pending.value) return
    pending.value = true
    error.value = null
    try {
      let accountId: number | undefined
      try {
        accountId = (await service.createFirstAccount()).account
      } catch (cause) {
        if (!(ApiError.isApiError(cause) && cause.code === ACCOUNT_ALREADY_EXISTS)) throw cause
      }
      await finish(accountId)
    } catch {
      error.value = 'auth.pendingInvites.continueError'
    } finally {
      pending.value = false
    }
  }

  return {
    invites,
    selectedIds,
    userEmail: account.userEmail,
    loading,
    pending,
    error,
    load,
    toggle,
    accept,
    continueWithoutInvites,
  }
}
